import React, { useState } from "react";
import Input from "../controls/Input";
import { useFormik } from "formik";
import * as yup from "yup";
import { PiPhone, PiPassword } from "react-icons/pi";
import { ForgotPassword, ForgotPasswordInitiate } from "../../configurations/crud/applicationUserCrud";
import LodingButton from "../controls/LodingButton";
import { Link } from "react-router-dom";
import { useToast } from "../controls/Toast/ToastService";
import { IoCheckmarkDoneCircleOutline } from "react-icons/io5";
import { MdErrorOutline, MdOutlineLocalPhone, MdOutlineVerifiedUser } from "react-icons/md";
import { FaPhoneAlt } from "react-icons/fa";
import { RiLockPasswordLine, RiShieldKeyholeLine } from "react-icons/ri";
import { TbPasswordFingerprint } from "react-icons/tb";
import { BsShieldCheck } from "react-icons/bs";
import useGlobalLoader from "../../configurations/useGlobalLoader";
import Card from "../controls/Card";
import MasterPage from "../app/MasterPage";

const schema = yup.object().shape({
  UserName: yup
    .string()
    .min(10, "Must be mobile number")
    .max(10, "Must be mobile number")
    .required("Mobile number is required."),
  OTP: yup.string().required("OTP is required."),
  NewPass: yup
    .string()
    .min(8, "Password must have 8 charactors")
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/, {
      message:
        "Password should contains one Capital letter, one small letter, one special charactor and a digit.",
    })
    .required("New password is required."),
  ConfirmPassword: yup
    .string()
    .oneOf(
      [yup.ref("NewPass"), null],
      "New password and Confirm password missmatch"
    )
    .required("Confirm password is required."),
});

/* ------------------------------------------------------------------ */
/*  Step indicator — same design language as CreateAccount             */
/* ------------------------------------------------------------------ */
const STEPS = [
  { id: 1, label: "Mobile", Icon: MdOutlineLocalPhone },
  { id: 2, label: "Verify OTP", Icon: BsShieldCheck },
  { id: 3, label: "New Password", Icon: RiShieldKeyholeLine },
];

function Stepper({ level }) {
  // level 0 = success (everything done)
  const current = level === 0 ? 4 : level;
  return (
    <div className="flex items-center justify-center w-full max-w-md mx-auto mb-6 px-2">
      {STEPS.map((step, i) => {
        const done = current > step.id;
        const active = current === step.id;
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center gap-1 shrink-0">
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-base sm:text-lg transition-all duration-300 border-2
                ${done ? "bg-sky-500 border-sky-500 text-white"
                  : active ? "bg-white border-sky-500 text-sky-500 shadow-[0_0_0_4px_rgba(47,180,255,0.2)]"
                    : "bg-white border-gray-300 text-gray-400"}`}>
                {done ? <IoCheckmarkDoneCircleOutline /> : <step.Icon />}
              </div>
              <span className={`text-[10px] sm:text-xs font-medium ${active || done ? "text-sky-600" : "text-gray-400"}`}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 &&
              <div className={`flex-1 h-0.5 mx-1 sm:mx-2 mb-4 rounded transition-all duration-500 ${current > step.id ? "bg-sky-500" : "bg-gray-200"}`} />
            }
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mobile number banner (shown on step 2, 3 & success)                */
/* ------------------------------------------------------------------ */
function MobileBanner({ number, onEdit, verified }) {
  return (
    <div className={`rounded-xl border p-3 text-center ${verified ? "bg-green-50 border-green-100" : "bg-sky-50 border-sky-100"}`}>
      <label className="block text-xs font-medium text-gray-500 mb-1">
        {verified ? "Mobile verified" : "OTP sent to"}
      </label>
      <h1 className={`text-2xl sm:text-3xl flex flex-wrap justify-center items-center gap-2 font-semibold ${verified ? "text-green-600" : "text-sky-700"}`}>
        {verified && <MdOutlineVerifiedUser />}
        <PiPhone /><span>{number}</span>
        {onEdit &&
          <button type="button" onClick={onEdit}
            className="text-sky-600 cursor-pointer hover:text-sky-500 text-sm underline underline-offset-2">
            Edit
          </button>
        }
      </h1>
    </div>
  );
}

export default function ForgetPassword() {
  const { start, end } = useGlobalLoader();
  const [level, setLevel] = useState(1);
  const [loding, setLoding] = useState(false);
  const toast = useToast();

  const {
    values,
    errors,
    isValid,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    setValues,
  } = useFormik({
    initialValues: {
      UserName: "",
      OTP: "",
      NewPass: "",
      ConfirmPassword: "",
    },
    validationSchema: schema,
    onSubmit(values) {
      handleForgetPassword(values);
    },
  });

  function handleForgetPassword(val) {
    setLoding(true);
    start();
    ForgotPassword(values).then(res => {
      if (res.data) {
        let data = res.data;
        if (data.succeeded) {
          setLevel(0);
          setValues({ ...values, OTP: "", NewPass: "", ConfirmPassword: "" });
          toast.open("Password has been changed successfully.", <IoCheckmarkDoneCircleOutline />, "success");
        } else {
          if (data.code === 108) {
            setLevel(2);
            toast.open(data.message, <MdErrorOutline />, "danger");
          }
          else {
            toast.open(data.message, <MdErrorOutline />, "danger");
          }
        }
      }
      else {
        toast.open("Server Error", <MdErrorOutline />, "danger");
      }
      setLoding(false);
      end();
    }).catch(() => {
      setLoding(false);
      end();
      toast.open("Internet not connected, please connect and refresh", <MdErrorOutline />, "danger", 5000);
    });
  }

  const handleInitiateForgrtPassword = () => {
    if (!values.UserName || values.UserName.length !== 10) {
      toast.open("Please enter a valid 10 digit mobile number", <MdErrorOutline />, "danger");
      return;
    }
    setLoding(true);
    start();
    ForgotPasswordInitiate(values).then(res => {
      if (res.data) {
        let data = res.data;
        if (data.result.succeeded) {
          setLevel(2);
          toast.open("Otp sent successfully.", <IoCheckmarkDoneCircleOutline />, "success");
        } else {
          toast.open(data.result.message, <MdErrorOutline />, "danger");
        }
      }
      else {
        toast.open("Server Error", <MdErrorOutline />, "danger");
      }
      setLoding(false);
      end();
    }).catch(() => {
      setLoding(false);
      end();
      toast.open("Internet not connected, please connect and refresh", <MdErrorOutline />, "danger", 5000);
    });
  };

  /* numeric-only handler for mobile & OTP fields */
  const onNumericChange = (e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, "");
    handleChange(e);
  };

  return (
    <MasterPage>
      <div className="flex flex-col min-h-full justify-center px-4 sm:px-6 py-10 lg:px-8 bg-gradient-to-b from-sky-50 via-slate-50 to-slate-100">
        <Stepper level={level} />
        <div className="mx-auto w-full max-w-sm">
          <Card title={"Forget Password"} isBorder={true} isBack={true}>
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="my-5 sm:mx-auto sm:w-full sm:max-w-sm">
                <div className="space-y-5">

                  {/* ---------------- STEP 1 : Mobile number ---------------- */}
                  {level === 1 &&
                    <>
                      <div className="text-center mb-1">
                        <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center text-white text-2xl shadow-lg shadow-sky-200 mb-3">
                          <RiShieldKeyholeLine />
                        </div>
                        <h1 className="text-xl font-bold text-gray-800">Reset your password</h1>
                        <p className="text-xs text-gray-500 mt-1">
                          Enter your registered mobile number and we'll send you an OTP.
                        </p>
                      </div>
                      <div>
                        <label htmlFor="UserName" className="block text-sm font-medium leading-6 text-gray-900">
                          Mobile no.
                        </label>
                        <div className="mt-2">
                          <Input
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="Enter 10 digit mobile no."
                            id="UserName"
                            Icon={<FaPhoneAlt className="text-sky-600 h-4 w-4" />}
                            value={values.UserName}
                            onChange={onNumericChange}
                            onBlur={handleBlur}
                            isError={errors.UserName && touched.UserName}
                          />
                          {errors.UserName && touched.UserName && (
                            <p className="text-red-600 text-xs mt-1">{errors.UserName}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        {!loding ?
                          <button
                            type="button"
                            className={errors.UserName || !values.UserName ? "btn-blue-disabled w-full" : "btn-blue w-full"}
                            disabled={!!errors.UserName || !values.UserName}
                            onClick={handleInitiateForgrtPassword}
                          >
                            Get OTP
                          </button>
                          :
                          <LodingButton text="Sending OTP" />
                        }
                        <p className="text-[11px] text-gray-400 text-center mt-3">
                          Remembered your password?{" "}
                          <Link to="/" className="text-sky-600 font-medium hover:text-sky-500 underline underline-offset-2">
                            Back to login
                          </Link>
                        </p>
                      </div>
                    </>
                  }

                  {/* ---------------- STEP 2 : Verify OTP ---------------- */}
                  {level === 2 &&
                    <>
                      <MobileBanner
                        number={values.UserName}
                        onEdit={() => { setLevel(1); setValues({ ...values, OTP: "" }); }}
                      />
                      <div>
                        <label htmlFor="OTP" className="block text-sm font-medium leading-6 text-gray-900">
                          OTP
                        </label>
                        <div className="mt-2">
                          <Input
                            placeholder="Enter OTP"
                            type="password"
                            inputMode="numeric"
                            id="OTP"
                            Icon={<TbPasswordFingerprint className="text-sky-500 h-4 w-4" />}
                            value={values.OTP}
                            onChange={onNumericChange}
                            onBlur={handleBlur}
                            isError={errors.OTP && touched.OTP}
                          />
                          {errors.OTP && touched.OTP && (
                            <p className="text-red-600 text-xs mt-1">{errors.OTP}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
                        <button
                          type="button"
                          className="hyper-link flex w-full justify-center cursor-pointer px-3 py-1.5 text-sm"
                          onClick={() => { setLevel(1); setValues({ ...values, OTP: "" }); }}
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          className={errors.OTP || !values.OTP ? "btn-blue-disabled w-full" : "btn-blue w-full"}
                          disabled={!!errors.OTP || !values.OTP}
                          onClick={() => setLevel(3)}
                        >
                          Verify OTP
                        </button>
                      </div>
                    </>
                  }

                  {/* ---------------- STEP 3 : New password ---------------- */}
                  {level === 3 &&
                    <>
                      <MobileBanner number={values.UserName} verified />
                      <div>
                        <label htmlFor="NewPass" className="block text-sm font-medium leading-6 text-gray-900">
                          New Password
                        </label>
                        <div className="mt-2">
                          <Input
                            placeholder="Enter new password eg: Abcd@123"
                            type="password"
                            id="NewPass"
                            Icon={<RiLockPasswordLine className="text-green-500 h-4 w-4" />}
                            value={values.NewPass}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isError={errors.NewPass && touched.NewPass}
                          />
                          {errors.NewPass && touched.NewPass && (
                            <p className="text-red-600 text-xs mt-1">{errors.NewPass}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label htmlFor="ConfirmPassword" className="block text-sm font-medium leading-6 text-gray-900">
                          Confirm Password
                        </label>
                        <div className="mt-2">
                          <Input
                            placeholder="Re-enter new password"
                            type="password"
                            id="ConfirmPassword"
                            Icon={<RiLockPasswordLine className="text-green-500 h-4 w-4" />}
                            value={values.ConfirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isError={errors.ConfirmPassword && touched.ConfirmPassword}
                          />
                          {errors.ConfirmPassword && touched.ConfirmPassword && (
                            <p className="text-red-600 text-xs mt-1">{errors.ConfirmPassword}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
                        <button
                          type="button"
                          className="hyper-link flex w-full justify-center cursor-pointer px-3 py-1.5 text-sm"
                          onClick={() => setLevel(2)}
                        >
                          Change OTP
                        </button>
                        {!loding ?
                          <button
                            className={!isValid ? "btn-blue-disabled w-full" : "btn-blue w-full"}
                            disabled={!isValid}
                            type="submit"
                          >
                            Reset Password
                          </button>
                          :
                          <LodingButton text={"Resetting"} />
                        }
                      </div>
                    </>
                  }

                  {/* ---------------- SUCCESS ---------------- */}
                  {level === 0 &&
                    <div className="flex flex-col items-center text-center gap-3 py-4">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-4xl animate-[pulse_1.5s_ease_2]">
                        <IoCheckmarkDoneCircleOutline />
                      </div>
                      <h1 className="text-xl font-bold text-gray-800">Password updated!</h1>
                      <p className="text-sm text-gray-500">
                        Your password for <span className="font-semibold text-gray-700">{values.UserName}</span> has been changed successfully.
                        You can now sign in with your new password.
                      </p>
                      <Link to="/" className="btn-blue w-full text-center mt-2">
                        Go to Login
                      </Link>
                    </div>
                  }

                </div>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </MasterPage>
  );
}